// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import { Link } from '@ta/common.routing';
import { ClickTracker } from '@ta/common.tracking';
import styles from './SimpleLinkSection.less';

export type SimpleLink = {|
  url: string,
  text: string,
  trackAction: string,
  newWindow: boolean,
  servletNameOverride: string | null,
  forceAjaxTrack: boolean,
  pid: number,
  idForJS: string,
|};

type ExternalProps = {|
  /* the links to display */
  links: SimpleLink[],
  /* some links support a idForJS because they
   * have a different behavior on click. This is behavior that is backward compatible with
   * the non-component implementation of the footer.  allowHandleJsClick allows a caller to control
   * whether this behavior is enabled for this set of links.
   */
  disallowHandleJsClick?: boolean,
  /* the id of the module to use for tracking */
  trackingModule?: string,
|};

type Props = {|
  ...ExternalProps,
  cookieDomain: string | null,
|};

const defaultProps = {
  disallowHandleJsClick: false,
  trackingModule: 'Footer',
};

/** A list of simple links for the footer */
const SimpleLinkSection = ({
  links,
  disallowHandleJsClick,
  trackingModule,
  cookieDomain,
}: Props) => {
  const clickTrackingAction = 'click';

  const handleJsClick = (e: SyntheticEvent<*>, link: SimpleLink) => {
    e.persist();
    // TODO: Help center will eventually be a part of this again. Code is in-flux, but removed most of that unused code for now.
    if (link.idForJS === 'help_center') {
      global.require(
        ['trjs!brand/HelpCenterOverlay', 'trcss!src/build/required/help_center_overlay'],
        o => {
          o.open(e.target, link.url);
        },
      );
    }
  };

  const linkClickHandler = (link: SimpleLink) => {
    if (link.pid) {
      const inFiveSeconds = new Date(new Date().getTime() + 5000);
      Cookies.set('NPID', link.pid, {
        expires: inFiveSeconds,
        path: '/',
        ...(cookieDomain ? { domain: cookieDomain } : null),
      });
    }
  };

  const linkList = links.map(link => {
    const useHandleJsClick = !disallowHandleJsClick && link.idForJS;

    return (
      <li key={link.url}>
        <ClickTracker
          module={trackingModule}
          action={clickTrackingAction}
          context={link.trackAction}
          forceAjax={link.forceAjaxTrack}
          dstServlet={link.servletNameOverride || undefined}
        >
          {useHandleJsClick ? (
            <span
              onClick={e => handleJsClick(e, link)}
              className={`ui_link ${styles.simpleFooterLink}`}
            >
              {link.text}
            </span>
          ) : (
            <Link
              href={link.url}
              className={`ui_link ${styles.simpleFooterLink}`}
              target={link.newWindow ? '_blank' : undefined}
              onClick={() => linkClickHandler(link)}
            >
              {link.text}
            </Link>
          )}
        </ClickTracker>
      </li>
    );
  });

  return <ul>{linkList}</ul>;
};

SimpleLinkSection.defaultProps = defaultProps;

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  cookieDomain: state.meta.cookieDomain,
}))(SimpleLinkSection);
